// The SSH key library's management home.
//
// The wizard only needs pick-or-create; renaming a key and finding out which
// workers would lose their redeploy if you deleted it belong somewhere an
// operator goes on purpose. Same shape as the Users card above it.
//
// Nothing here can show a key's material, because no endpoint returns it. What a
// row can show is enough to tell two keys apart — a name the operator chose and
// the key's own fingerprint — plus what would break without it.

import { zodResolver } from "@hookform/resolvers/zod";
import { SshKeyNameSchema } from "@schemas";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Checkbox } from "@veil/ui/components/checkbox";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "@veil/ui/components/field";
import {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogCancel,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatRelative } from "@veil/ui/lib/format";
import KeyRound from "lucide-react/dist/esm/icons/key-round";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import Pencil from "lucide-react/dist/esm/icons/pencil";
import Plus from "lucide-react/dist/esm/icons/plus";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { PrivateKeyField } from "@/components/form/private-key-field";
import { TextField } from "@/components/form/text-field";
import { EmptyState, QueryError } from "@/components/query-state";
import { api, unwrap } from "@/lib/api";
import { privateKeyIssue } from "@/lib/private-key-shape";
import {
	SSH_KEYS_QUERY_KEY,
	type SshKeyMetadata,
	shortFingerprint,
	useSshKeys,
} from "@/lib/ssh-keys";

// Every field is a required string, so this schema's input and output types are
// the same one — which is what react-hook-form needs of a resolver. The wire
// schema's `.default()` on sshUser and its passphrase transform would split
// them, so the defaults live in ADD_KEY_DEFAULTS and the empty passphrase is
// normalised at submit instead.
const AddKeyFormSchema = z.object({
	name: SshKeyNameSchema,
	sshUser: z.string().trim().min(1, "Give the login user").max(64),
	privateKey: z.string().superRefine((value, ctx) => {
		const issue = privateKeyIssue(value);
		if (issue) ctx.addIssue({ code: "custom", message: issue });
	}),
	passphrase: z.string(),
});
type AddKeyForm = z.infer<typeof AddKeyFormSchema>;

const ADD_KEY_DEFAULTS: AddKeyForm = {
	name: "",
	sshUser: "root",
	privateKey: "",
	passphrase: "",
};

const RenameFormSchema = z.object({ name: SshKeyNameSchema });
type RenameForm = z.infer<typeof RenameFormSchema>;

export function SshKeysCard({ canEdit }: { canEdit: boolean }) {
	const keys = useSshKeys();
	const [adding, setAdding] = useState(false);
	const [renaming, setRenaming] = useState<SshKeyMetadata | null>(null);
	const [removing, setRemoving] = useState<SshKeyMetadata | null>(null);

	return (
		<Card>
			<CardHeader className="flex flex-row items-start justify-between gap-3">
				<div>
					<CardTitle className="flex items-center gap-2 text-base">
						<KeyRound className="size-4" /> SSH keys
					</CardTitle>
					<p className="mt-1 text-xs text-muted-foreground">
						Keys the manager can open a worker host with. Adding one here means
						the next remote deploy picks it by name instead of pasting it again,
						and that a worker holding it can be redeployed or uninstalled in a
						click.
					</p>
				</div>
				{canEdit ? (
					<Button size="sm" onClick={() => setAdding(true)}>
						<Plus data-icon="inline-start" /> Add key
					</Button>
				) : null}
			</CardHeader>
			<CardContent className="p-0">
				{keys.isLoading ? <Skeleton className="m-6 h-24" /> : null}
				{!keys.isLoading && keys.isError ? (
					<div className="p-6">
						<QueryError
							title="Couldn't load SSH keys"
							error={keys.error}
							onRetry={() => void keys.refetch()}
							retrying={keys.isFetching}
						/>
					</div>
				) : null}
				{!keys.isLoading && !keys.isError && (keys.data ?? []).length === 0 ? (
					<div className="p-6">
						<EmptyState
							icon={KeyRound}
							title="No saved keys"
							description="Deploying a worker to another machine will offer to save the key it uses. Nothing is stored until you ask for it."
						/>
					</div>
				) : null}
				{!keys.isLoading && !keys.isError && (keys.data ?? []).length > 0 ? (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Fingerprint</TableHead>
								<TableHead>User</TableHead>
								<TableHead>Used by</TableHead>
								<TableHead>Last used</TableHead>
								<TableHead className="text-right" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{(keys.data ?? []).map((key) => (
								<TableRow key={key.id}>
									<TableCell className="font-medium">{key.name}</TableCell>
									<TableCell
										className="font-mono text-xs text-muted-foreground"
										title={key.fingerprint ?? undefined}
									>
										{shortFingerprint(key.fingerprint)}
									</TableCell>
									<TableCell className="font-mono text-xs">
										{key.sshUser}
									</TableCell>
									<TableCell>
										{key.usedBy.length === 0 ? (
											<span className="text-xs text-muted-foreground">
												no workers
											</span>
										) : (
											<div className="flex flex-wrap gap-1">
												{key.usedBy.map((hostname) => (
													<Badge
														key={hostname}
														variant="outline"
														className="font-mono text-[10px]"
													>
														{hostname}
													</Badge>
												))}
											</div>
										)}
									</TableCell>
									<TableCell className="text-xs text-muted-foreground">
										{key.lastUsedAt ? formatRelative(key.lastUsedAt) : "never"}
									</TableCell>
									<TableCell className="text-right">
										{canEdit ? (
											<div className="flex justify-end gap-1">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => setRenaming(key)}
													aria-label={`Rename ${key.name}`}
													title={`Rename ${key.name}`}
												>
													<Pencil />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => setRemoving(key)}
													aria-label={`Delete ${key.name}`}
													title={`Delete ${key.name}`}
												>
													<Trash2 />
												</Button>
											</div>
										) : null}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				) : null}
			</CardContent>

			<AddKeyDialog open={adding} onOpenChange={setAdding} />
			<RenameKeyDialog
				// Remounts on a different target so the form picks up fresh defaults
				// without a reset effect.
				key={renaming?.id ?? "rename-closed"}
				target={renaming}
				onClose={() => setRenaming(null)}
			/>
			<DeleteKeyDialog target={removing} onClose={() => setRemoving(null)} />
		</Card>
	);
}

function AddKeyDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const queryClient = useQueryClient();
	const form = useForm<AddKeyForm>({
		resolver: zodResolver(AddKeyFormSchema),
		mode: "onTouched",
		defaultValues: ADD_KEY_DEFAULTS,
	});

	const create = useMutation({
		mutationFn: (values: AddKeyForm) =>
			unwrap(
				api.api["ssh-keys"].post({
					name: values.name,
					sshUser: values.sshUser,
					privateKey: values.privateKey,
					// An untouched password field sends "", which is "no passphrase"
					// rather than a passphrase of zero length.
					passphrase: values.passphrase.length > 0 ? values.passphrase : null,
				}),
			),
		onSuccess: (_data, values) => {
			toast.success(`Saved ${values.name}`);
			queryClient.invalidateQueries({ queryKey: SSH_KEYS_QUERY_KEY });
			form.reset(ADD_KEY_DEFAULTS);
			onOpenChange(false);
		},
		onError: (err) => {
			const message = err instanceof Error ? err.message : "Could not save";
			// A name collision is about the field in front of them, not the cluster.
			if (/name/i.test(message) && /(exists|taken|use)/i.test(message)) {
				form.setError("name", { type: "server", message });
				return;
			}
			toast.error(message);
		},
	});

	return (
		<ResponsiveDialog open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent className="sm:max-w-lg">
				<ResponsiveDialogHeader>
					<ResponsiveDialogTitle>Add an SSH key</ResponsiveDialogTitle>
					<ResponsiveDialogDescription>
						Stored encrypted. No endpoint returns it afterwards — not to this
						page and not to the wizard — so a saved key can only be used by
						name, which is what keeps the secret out of the browser on every
						deploy after this one.
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>
				<form
					onSubmit={form.handleSubmit((values) => create.mutate(values))}
					noValidate
					className="contents"
				>
					<FieldGroup>
						<TextField
							id="ssh-key-name"
							label="Name"
							placeholder="hetzner root"
							autoComplete="off"
							error={form.formState.errors.name}
							description="How this key is listed everywhere else. Name it after the machines it opens."
							{...form.register("name")}
						/>
						<TextField
							id="ssh-key-user"
							label="Default login user"
							placeholder="root"
							autoComplete="off"
							spellCheck={false}
							className="max-w-xs"
							error={form.formState.errors.sshUser}
							description="Prefilled when this key is picked. Needs to be root, or have passwordless sudo."
							{...form.register("sshUser")}
						/>
						<PrivateKeyField
							id="ssh-key-material"
							error={form.formState.errors.privateKey}
							registration={form.register("privateKey")}
							onFileText={(text) =>
								form.setValue("privateKey", text, {
									shouldValidate: true,
									shouldDirty: true,
								})
							}
						/>
						<TextField
							id="ssh-key-passphrase"
							label="Key passphrase"
							type="password"
							autoComplete="off"
							className="max-w-xs"
							error={form.formState.errors.passphrase}
							description="Only if the key is encrypted."
							{...form.register("passphrase")}
						/>
					</FieldGroup>
					<ResponsiveDialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={create.isPending}>
							{create.isPending ? (
								<Loader2 data-icon="inline-start" className="animate-spin" />
							) : null}
							{create.isPending ? "Saving…" : "Save key"}
						</Button>
					</ResponsiveDialogFooter>
				</form>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

function RenameKeyDialog({
	target,
	onClose,
}: {
	target: SshKeyMetadata | null;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const form = useForm<RenameForm>({
		resolver: zodResolver(RenameFormSchema),
		mode: "onTouched",
		defaultValues: { name: target?.name ?? "" },
	});

	const rename = useMutation({
		mutationFn: (values: RenameForm) =>
			unwrap(api.api["ssh-keys"]({ id: target?.id ?? "" }).patch(values)),
		onSuccess: (_data, values) => {
			toast.success(`Renamed to ${values.name}`);
			queryClient.invalidateQueries({ queryKey: SSH_KEYS_QUERY_KEY });
			onClose();
		},
		onError: (err) => {
			const message = err instanceof Error ? err.message : "Rename failed";
			if (/name/i.test(message) && /(exists|taken|use)/i.test(message)) {
				form.setError("name", { type: "server", message });
				return;
			}
			toast.error(message);
		},
	});

	return (
		<ResponsiveDialog
			open={target !== null}
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<ResponsiveDialogContent className="sm:max-w-md">
				<ResponsiveDialogHeader>
					<ResponsiveDialogTitle>Rename this key</ResponsiveDialogTitle>
					<ResponsiveDialogDescription>
						The name is all that changes. Rotating a key means adding a new one,
						because swapping the material underneath{" "}
						{target && target.usedBy.length > 0
							? `${target.usedBy.join(", ")} would change what a redeploy authenticates with`
							: "a worker would change what a redeploy authenticates with"}
						.
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>
				<form
					onSubmit={form.handleSubmit((values) => rename.mutate(values))}
					noValidate
					className="contents"
				>
					<TextField
						id="ssh-key-rename"
						label="Name"
						autoComplete="off"
						error={form.formState.errors.name}
						{...form.register("name")}
					/>
					<ResponsiveDialogFooter>
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" disabled={rename.isPending}>
							{rename.isPending ? "Saving…" : "Save name"}
						</Button>
					</ResponsiveDialogFooter>
				</form>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

/**
 * Delete, with the consequence named.
 *
 * The workers survive a deleted key — the column is set null rather than
 * cascading — so this is not "you will lose these workers". It is "these workers
 * will stop being redeployable", and the difference is worth the sentence.
 */
function DeleteKeyDialog({
	target,
	onClose,
}: {
	target: SshKeyMetadata | null;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const [force, setForce] = useState(false);
	const inUse = (target?.usedBy.length ?? 0) > 0;

	const remove = useMutation({
		mutationFn: (key: SshKeyMetadata) =>
			unwrap(
				api.api["ssh-keys"]({ id: key.id }).delete(undefined, {
					query: inUse ? { force: "1" } : {},
				}),
			),
		onSuccess: (_data, key) => {
			toast.success(`Deleted ${key.name}`);
			queryClient.invalidateQueries({ queryKey: SSH_KEYS_QUERY_KEY });
			// A worker's redeploy affordance hangs off sshKeyId, which just changed.
			queryClient.invalidateQueries({ queryKey: ["workers"] });
			close();
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Delete failed"),
	});

	function close() {
		setForce(false);
		onClose();
	}

	return (
		<ResponsiveDialog
			variant="alert"
			open={target !== null}
			onOpenChange={(open) => {
				if (!open) close();
			}}
		>
			<ResponsiveDialogContent>
				<ResponsiveDialogHeader>
					<ResponsiveDialogTitle>
						Delete <span className="font-mono">{target?.name}</span>?
					</ResponsiveDialogTitle>
					<ResponsiveDialogDescription>
						{inUse ? (
							<>
								{target?.usedBy.join(", ")}{" "}
								{target?.usedBy.length === 1 ? "holds" : "hold"} this key.{" "}
								{target?.usedBy.length === 1 ? "It keeps" : "They keep"} running
								and {target?.usedBy.length === 1 ? "stays" : "stay"} in the
								pool, but the manager loses the only way it can reach{" "}
								{target?.usedBy.length === 1 ? "that host" : "those hosts"} over
								SSH: no one-click redeploy, and removing{" "}
								{target?.usedBy.length === 1 ? "the worker" : "them"} will leave
								the containers running on the far side.
							</>
						) : (
							<>
								No worker holds this key, so nothing loses access. The key
								material is gone for good — this manager never had a copy it
								could show you, and it will not have one after this.
							</>
						)}
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>
				{inUse ? (
					<Field orientation="horizontal" className="rounded-md border p-3">
						<Checkbox
							id="ssh-key-delete-force"
							checked={force}
							onCheckedChange={(v) => setForce(v === true)}
						/>
						<FieldContent>
							<FieldLabel htmlFor="ssh-key-delete-force">
								Delete it anyway
							</FieldLabel>
							<FieldDescription>
								Confirms you have another way onto{" "}
								{target?.usedBy.length === 1
									? "that machine"
									: "those machines"}
								.
							</FieldDescription>
						</FieldContent>
					</Field>
				) : null}
				<ResponsiveDialogFooter>
					<ResponsiveDialogCancel disabled={remove.isPending}>
						Cancel
					</ResponsiveDialogCancel>
					<ResponsiveDialogAction
						variant="destructive"
						disabled={remove.isPending || (inUse && !force)}
						onClick={(e) => {
							e.preventDefault();
							if (target) remove.mutate(target);
						}}
					>
						{remove.isPending ? "Deleting…" : "Delete key"}
					</ResponsiveDialogAction>
				</ResponsiveDialogFooter>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
